import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, KpiCard, StatusBadge } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { Link } from 'react-router-dom';
import { Users, UserCog, Briefcase, Clock, CheckCircle, AlertCircle, Calendar, ChevronRight, CalendarPlus, CalendarCheck2, Unlink } from 'lucide-react';
import { statusLabel } from '../../lib/constants';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [calStatus, setCalStatus] = useState({ loaded: false, configured: false, connected: false, google_email: null });

  const loadCalendarStatus = async () => {
    try {
      const r = await api.get('/oauth/calendar/status');
      setCalStatus({ loaded: true, ...r.data });
    } catch (err) {
      setCalStatus({ loaded: true, configured: false, connected: false, google_email: null });
    }
  };

  useEffect(() => {
    (async () => {
      const [s, j] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/jobs'),
      ]);
      setStats(s.data);
      setRecentJobs(j.data.slice(0, 5));
    })();
    loadCalendarStatus();

    // Handle OAuth callback redirect from Google
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === '1') {
      toast.success('Google Kalender erfolgreich verbunden');
      window.history.replaceState({}, '', window.location.pathname);
      loadCalendarStatus();
    }
    if (params.get('calendar_error')) {
      toast.error(`Google-Verbindung fehlgeschlagen: ${params.get('calendar_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const connectGoogleCalendar = async () => {
    try {
      const res = await api.get('/oauth/calendar/login', { params: { origin: window.location.origin } });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Google-Verbindung konnte nicht gestartet werden');
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('Google-Kalender-Verbindung wirklich aufheben?')) return;
    try {
      await api.delete('/oauth/calendar/disconnect');
      toast.success('Verbindung getrennt');
      loadCalendarStatus();
    } catch (err) {
      toast.error('Trennen fehlgeschlagen');
    }
  };

  if (!stats) return <AppLayout><div>Lade...</div></AppLayout>;

  const s = stats.jobs_by_status || {};
  const newCount = s.neu || 0;
  const inProgress = (s.in_bearbeitung || 0) + (s.dolmetscher_angefragt || 0);
  const confirmed = (s.dolmetscher_bestaetigt || 0) + (s.bestaetigt || 0) + (s.meetinglink_versendet || 0);
  const running = s.in_durchfuehrung || 0;
  const completed = s.abgeschlossen || 0;

  return (
    <AppLayout>
      <PageHeader title="Dashboard" description="Übersicht über alle Aufträge und Aktivitäten" />

      {stats.pending_interpreters > 0 && (
        <Link to="/admin/approvals" data-testid="pending-banner" className="block mb-6 bg-amber-50 border border-amber-200 rounded-md px-5 py-4 hover:bg-amber-100 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-700" />
              <div>
                <div className="font-medium text-amber-900 text-sm">
                  {stats.pending_interpreters} Dolmetscher {stats.pending_interpreters === 1 ? 'wartet' : 'warten'} auf Freischaltung
                </div>
                <div className="text-xs text-amber-700">Klicken Sie hier, um die Anträge zu überprüfen.</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-700" />
          </div>
        </Link>
      )}

      {/* Google Calendar Status */}
      {calStatus.loaded && (
        <div data-testid="google-cal-status-card" className="mb-6 bg-white border border-slate-200 rounded-md card-subtle px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${calStatus.connected ? 'bg-emerald-50' : 'bg-slate-100'}`}>
              {calStatus.connected
                ? <CalendarCheck2 className="w-5 h-5 text-emerald-600" />
                : <Calendar className="w-5 h-5 text-slate-400" />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">
                Google Kalender
                {!calStatus.configured && (
                  <span className="ml-2 text-xs text-amber-600 font-normal">— nicht konfiguriert</span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {!calStatus.configured
                  ? 'Bitte Backend-Variablen GOOGLE_CALENDAR_CLIENT_ID/SECRET setzen.'
                  : calStatus.connected
                    ? <>Verbunden mit <span className="font-medium text-slate-700">{calStatus.google_email}</span> · Termine + Meet-Links können erstellt werden.</>
                    : 'Verbinden Sie Ihren Google-Kalender, um Termine zu synchronisieren und Google Meet-Links automatisch zu erstellen.'}
              </div>
            </div>
          </div>
          {calStatus.configured && (
            calStatus.connected ? (
              <button
                onClick={disconnectGoogleCalendar}
                data-testid="google-cal-disconnect-btn"
                className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 rounded-md px-3 py-1.5 transition-colors shrink-0"
              >
                <Unlink className="w-3.5 h-3.5" /> Verbindung trennen
              </button>
            ) : (
              <button
                onClick={connectGoogleCalendar}
                data-testid="google-cal-connect-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#001d4c] hover:bg-[#0a2c63] text-white rounded-md px-3.5 py-2 transition-colors shrink-0"
              >
                <CalendarPlus className="w-4 h-4" /> Jetzt verbinden
              </button>
            )
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <KpiCard label="Neue Anfragen" value={newCount} icon={AlertCircle} testid="kpi-new" />
        <KpiCard label="In Bearbeitung" value={inProgress} icon={Clock} testid="kpi-inprogress" />
        <KpiCard label="Bestätigt" value={confirmed} icon={CheckCircle} testid="kpi-confirmed" />
        <KpiCard label="Aktive Dolmetscher" value={stats.active_interpreters} icon={UserCog} testid="kpi-interpreters" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md card-subtle">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-heading text-base font-medium text-slate-900">Neueste Aufträge</h2>
            <Link to="/admin/jobs" className="text-xs text-[#0F4C81] hover:underline flex items-center gap-1" data-testid="link-all-jobs">Alle anzeigen <ChevronRight className="w-3 h-3" /></Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Auftrag</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Sprache</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Datum</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentJobs.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-8 text-sm text-slate-400">Noch keine Aufträge vorhanden</td></tr>
                ) : recentJobs.map(j => (
                  <tr key={j.id} className="interactive-row">
                    <td className="border-b border-slate-100 py-3 px-4">
                      <Link to={`/admin/jobs/${j.id}`} className="text-sm text-slate-900 font-mono hover:text-[#0F4C81]" data-testid={`job-link-${j.id}`}>{j.job_number || j.id.slice(0, 12)}</Link>
                    </td>
                    <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{j.source_language} → {j.target_language}</td>
                    <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{j.date} {j.time}</td>
                    <td className="border-b border-slate-100 py-3 px-4"><StatusBadge status={j.status} label={statusLabel(j.status)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md card-subtle">
          <div className="px-5 py-4 border-b border-slate-200">
            <h2 className="font-heading text-base font-medium text-slate-900">Status-Übersicht</h2>
          </div>
          <div className="p-5 space-y-3">
            {Object.entries(s).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{statusLabel(k)}</span>
                <span className="font-mono font-medium text-slate-900">{v}</span>
              </div>
            ))}
            {Object.values(s).every(v => v === 0) && <div className="text-sm text-slate-400 text-center py-4">Keine Aufträge</div>}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default AdminDashboard;
